/**
 * =============================================================================
 * COMPUTE ENGINE
 * =============================================================================
 * Pure functions — no React, no state, no HTTP.
 * Resolves a MetricCardInstance's extractions and fieldBindings
 * given a map of API responses.
 *
 * Entry points used by useMetricCard:
 *   applyContextTokens   — replaces {{token}} in endpoints and params
 *   resolveExtractions   — Phase 1: named values from API responses
 *   resolveFieldBindings — Phase 2: card field values from named extractions
 */

import type {
    AggregationFn,
    ExtractionDef,
    FieldBinding,
    FieldDataSource,
    MetricCardInstance,
    PerItemNode,
    TrendFieldBinding,
} from '@diagon-alley/common';
import type { MetricTrend } from '../components/MissionControl/types';

// =============================================================================
// DATE HELPERS
// =============================================================================

const formatDate = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

// =============================================================================
// PATH HELPERS
// =============================================================================

/** Navigate a dot-notation path, e.g. 'data.total' or 'blended_days_on_hand' */
const getNestedValue = (obj: unknown, path: string): unknown => {
    if (!path) return obj;
    return path.split('.').reduce((current: unknown, key: string) => {
        if (current == null || typeof current !== 'object') return undefined;
        return (current as Record<string, unknown>)[key];
    }, obj);
};

/**
 * Navigate a path that may include array index notation, e.g. 'plan[0].in_transit'
 * Used when extracting a field from each item in an array.
 */
const getNestedFieldWithIndex = (obj: unknown, path: string): unknown => {
    const parts = path.split('.');
    let current: unknown = obj;

    for (const part of parts) {
        if (current == null) return undefined;
        const arrayMatch = part.match(/^(\w+)\[(\d+)\]$/);
        if (arrayMatch) {
            const [, fieldName, indexStr] = arrayMatch;
            const arr = (current as Record<string, unknown>)[fieldName];
            if (!Array.isArray(arr)) return undefined;
            current = arr[parseInt(indexStr, 10)];
        } else {
            current = (current as Record<string, unknown>)[part];
        }
    }

    return current;
};

// =============================================================================
// PHASE 0 — CONTEXT TOKEN RESOLUTION
// =============================================================================

const replaceTokensInString = (
    str: string,
    tokens: Record<string, string>,
): string => {
    return str.replace(/\{\{([^}]+)\}\}/g, (match, key: string) => {
        const daysAgoMatch = key.match(/^daysAgo:(\d+)$/);
        if (daysAgoMatch) {
            const n = parseInt(daysAgoMatch[1], 10);
            const d = new Date();
            d.setDate(d.getDate() - n);
            return formatDate(d);
        }
        return tokens[key] ?? match;
    });
};

const replaceTokensInValue = (
    value: unknown,
    tokens: Record<string, string>,
): unknown => {
    if (typeof value === 'string') return replaceTokensInString(value, tokens);
    if (Array.isArray(value)) return value.map((v) => replaceTokensInValue(v, tokens));
    if (value !== null && typeof value === 'object') {
        const result: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
            result[k] = replaceTokensInValue(v, tokens);
        }
        return result;
    }
    return value;
};

/**
 * Resolves all {{token}} placeholders in any plain object.
 * Handles today, yesterday, daysAgo:N recursively across nested strings.
 * Use this to sanitise widget request payloads before storing them in
 * widget context — ensures the agent always receives real date strings
 * instead of raw template literals like {{daysAgo:180}}.
 */
export const resolvePayloadTokens = (
    payload: Record<string, unknown>,
): Record<string, unknown> => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const tokens: Record<string, string> = {
        today: formatDate(today),
        yesterday: formatDate(yesterday),
    };

    return replaceTokensInValue(payload, tokens) as Record<string, unknown>;
};

/**
 * Resolves all {{token}} placeholders in an instance's apiCalls
 * (both endpoint strings and param values) before fetching.
 *
 * Built-in tokens: today, yesterday, daysAgo:N
 * Context tokens:  any key/value from the context prop (e.g. vendorId)
 */
export const applyContextTokens = (
    instance: MetricCardInstance,
    context?: Record<string, string>,
): MetricCardInstance => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const tokens: Record<string, string> = {
        today: formatDate(today),
        yesterday: formatDate(yesterday),
        ...context,
    };

    return {
        ...instance,
        apiCalls: instance.apiCalls.map((call) => ({
            ...call,
            endpoint: replaceTokensInString(call.endpoint, tokens),
            params: call.params
                ? (replaceTokensInValue(call.params, tokens) as Record<string, unknown>)
                : undefined,
        })),
    };
};

// =============================================================================
// PHASE 1 — EXTRACTION
// =============================================================================

/** Evaluate a PerItemNode tree against one array item */
const executePerItem = (node: PerItemNode, item: unknown): unknown => {
    const resolvedArgs = node.args.map((arg) => {
        if (typeof arg === 'string') {
            if (arg.startsWith('item.')) {
                return getNestedFieldWithIndex(item, arg.slice(5));
            }
            const asNum = Number(arg);
            return isNaN(asNum) ? arg : asNum;
        }
        return executePerItem(arg, item);
    });

    switch (node.fn) {
        case 'DATE_DIFF': {
            const [a, b] = resolvedArgs;
            if (!a || !b) return null;
            const dateA = new Date(a as string);
            const dateB = new Date(b as string);
            if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return null;
            const diffMs = dateA.getTime() - dateB.getTime();
            const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
            return days >= 0 ? days : null;
        }
        case 'COALESCE':
            return resolvedArgs.find((a) => a !== null && a !== undefined) ?? null;
        case 'ADD':
            return (resolvedArgs[0] as number) + (resolvedArgs[1] as number);
        case 'SUBTRACT':
            return (resolvedArgs[0] as number) - (resolvedArgs[1] as number);
        case 'MULTIPLY':
            return (resolvedArgs[0] as number) * (resolvedArgs[1] as number);
        case 'DIVIDE':
            return resolvedArgs[1]
                ? (resolvedArgs[0] as number) / (resolvedArgs[1] as number)
                : null;
        case 'ABS':
            return Math.abs(resolvedArgs[0] as number);
        default:
            return null;
    }
};

/** Collapse an array of values to a scalar using an aggregation function */
const applyAggregate = (values: unknown[], fn: AggregationFn): unknown => {
    const nums = values.filter((v): v is number => typeof v === 'number');

    switch (fn) {
        case 'SUM':
            return nums.reduce((a, b) => a + b, 0);
        case 'AVERAGE':
            return nums.length > 0 ? nums.reduce((a, b) => a + b, 0) / nums.length : null;
        case 'MIN':
            return nums.length > 0 ? Math.min(...nums) : null;
        case 'MAX':
            return nums.length > 0 ? Math.max(...nums) : null;
        case 'COUNT':
            return values.length;
        case 'FIRST':
            return values[0] ?? null;
        case 'MOST_FREQUENT': {
            const counts = new Map<string, number>();
            for (const v of values) {
                const key = String(v);
                counts.set(key, (counts.get(key) ?? 0) + 1);
            }
            let max = 0;
            let result: string | null = null;
            for (const [k, count] of counts) {
                if (count > max) {
                    max = count;
                    result = k;
                }
            }
            return result;
        }
        default:
            return null;
    }
};

/**
 * Phase 1 — resolve all extractions into named variables.
 * Returns a Map of name → resolved value.
 */
export const resolveExtractions = (
    extractions: ExtractionDef[],
    responses: Map<string, unknown>,
): Map<string, unknown> => {
    const result = new Map<string, unknown>();

    for (const extraction of extractions) {
        const response = responses.get(extraction.from);
        if (response === undefined) continue;

        const atPath = getNestedValue(response, extraction.path);

        // Scalar pick — no array processing needed
        if (!extraction.field && !extraction.perItem) {
            result.set(extraction.name, atPath);
            continue;
        }

        if (!Array.isArray(atPath)) {
            result.set(extraction.name, null);
            continue;
        }

        // Compute a value from each item
        let itemValues: unknown[];
        if (extraction.perItem) {
            itemValues = atPath.map((item) => executePerItem(extraction.perItem!, item));
        } else {
            itemValues = atPath.map((item) => getNestedFieldWithIndex(item, extraction.field!));
        }

        const validValues = itemValues.filter((v) => v !== null && v !== undefined);

        if (!extraction.aggregate) {
            // Return as array (chart series use case)
            result.set(extraction.name, validValues);
        } else {
            result.set(extraction.name, applyAggregate(validValues, extraction.aggregate));
        }
    }

    return result;
};

// =============================================================================
// PHASE 2 — FIELD BINDING RESOLUTION
// =============================================================================

/** Resolve a single FieldDataSource to its final value */
const resolveSource = (
    source: FieldDataSource,
    extractions: Map<string, unknown>,
): unknown => {
    switch (source.type) {
        case 'literal':
            return source.value;
        case 'ref':
            return extractions.get(source.name) ?? null;
        case 'computed': {
            const args = source.args.map((arg) => resolveSource(arg, extractions));
            switch (source.fn) {
                case 'ADD':
                    if (args[0] == null || args[1] == null) return null;
                    return (args[0] as number) + (args[1] as number);
                case 'SUBTRACT':
                    if (args[0] == null || args[1] == null) return null;
                    return (args[0] as number) - (args[1] as number);
                case 'MULTIPLY':
                    if (args[0] == null || args[1] == null) return null;
                    return (args[0] as number) * (args[1] as number);
                case 'DIVIDE':
                    if (args[0] == null || !args[1]) return null;
                    return (args[0] as number) / (args[1] as number);
                case 'ABS':
                    if (args[0] == null) return null;
                    return Math.abs(args[0] as number);
                case 'ROUND':
                    if (args[0] == null) return null;
                    return Math.round(args[0] as number);
                case 'FIXED':
                    if (args[0] == null) return null;
                    return Number((args[0] as number).toFixed((args[1] as number) ?? 0));
                case 'PERCENTAGE_CHANGE': {
                    const [a, b] = args as [number, number];
                    if (a == null || b == null || b === 0) return null;
                    return ((a - b) / b) * 100;
                }
                case 'COALESCE':
                    return args.find((a) => a !== null && a !== undefined) ?? null;
                case 'FORMAT': {
                    const [template, ...values] = args as [string, ...unknown[]];
                    return template.replace(/\{\{(\d+)\}\}/g, (_, i: string) =>
                        String(values[parseInt(i, 10)] ?? ''),
                    );
                }
                case 'CONCAT':
                    return args.map(String).join('');
                case 'SIGN_WORD':
                    return (args[0] as number) >= 0 ? 'more' : 'less';
                default:
                    return null;
            }
        }
    }
};

/** Assemble a MetricTrend object from a TrendFieldBinding */
const resolveTrendBinding = (
    binding: TrendFieldBinding,
    extractions: Map<string, unknown>,
): MetricTrend | null => {
    const raw = extractions.get(binding.valueRef);
    if (typeof raw !== 'number') return null;

    const absValue = Math.abs(raw);
    const direction = raw >= 0 ? 'up' : 'down';

    let displayText: string;
    if (binding.labelTemplate) {
        displayText = binding.labelTemplate
            .replace('{{absValue}}', absValue.toFixed(1))
            .replace('{{direction}}', direction);
    } else {
        displayText = `${absValue.toFixed(1)}%`;
    }

    return {
        direction,
        value: displayText,
        positiveIsGood: binding.positiveIsGood,
    };
};

export interface ResolvedMetricCardFields {
    label: string | null;
    value: number | null;
    trend: MetricTrend | null;
    subtext: string | null;
}

/**
 * Phase 2 — resolve all fieldBindings into the 4 card slots.
 * Unknown fieldNames are silently ignored (future extensibility).
 */
export const resolveFieldBindings = (
    bindings: FieldBinding[],
    extractions: Map<string, unknown>,
): ResolvedMetricCardFields => {
    const fields: ResolvedMetricCardFields = {
        label: null,
        value: null,
        trend: null,
        subtext: null,
    };

    for (const binding of bindings) {
        if (binding.fieldName === 'trend') {
            fields.trend = resolveTrendBinding(binding, extractions);
        } else {
            const resolved = resolveSource(binding.value, extractions);
            if (binding.fieldName === 'label') fields.label = resolved as string | null;
            if (binding.fieldName === 'value') fields.value = resolved as number | null;
            if (binding.fieldName === 'subtext') fields.subtext = resolved as string | null;
        }
    }

    return fields;
};
